import { useState, useEffect, useRef } from 'react';
import { Bot, GitBranch, Plug, FlaskConical, ArrowRight, X } from 'lucide-react';
import { services } from '@/data/siteData';
import { scrollReveal, staggerFadeIn, prefersReducedMotion } from '@/hooks/useGsap';

const iconMap: Record<string, React.ReactNode> = {
  bot: <Bot className="h-6 w-6" />,
  workflow: <GitBranch className="h-6 w-6" />,
  plug: <Plug className="h-6 w-6" />,
  flask: <FlaskConical className="h-6 w-6" />,
};

const serviceFlows: Record<string, {
  steps: { label: string; desc: string; type: 'trigger' | 'ai' | 'action' | 'branch' }[];
  logs: string[];
}> = {
  'ai-automation': {
    steps: [
      { label: 'Webhook Trigger', desc: 'Menerima chat pelanggan 🌐', type: 'trigger' },
      { label: 'ChatGPT Classifier', desc: 'Analisis intent & sentimen 🧠', type: 'ai' },
      { label: 'CRM Sync & Alert', desc: 'Update data + notify Telegram 🚀', type: 'action' },
    ],
    logs: [
      'STATUS: Flow "Customer Auto-Reply" is ACTIVE',
      '[12:04:10] HTTP POST /webhook received from client...',
      '[12:04:11] Payload: "Mau nanya harga paket enterprise..."',
      '[12:04:11] Sending query to Claude Fable 5...',
      '[12:04:13] AI Classified: Intent=EnterpriseInquiry, Sentiment=Positive',
      '[12:04:13] Database: Created new contact lead "Enterprise-0819"',
      '[12:04:14] Telegram: Notification sent to sales team.',
      'SUCCESS: Execution completed in 3.82s ✔'
    ]
  },
  'workflow-optimization': {
    steps: [
      { label: 'Form Submitted', desc: 'Input data dari Typeform 📋', type: 'trigger' },
      { label: 'Router / Switch', desc: 'Cabang berdasarkan budget ⑃', type: 'branch' },
      { label: 'Auto-Generate PDF', desc: 'Bikin proposal otomatis 🖨', type: 'action' },
    ],
    logs: [
      'STATUS: Flow "Proposal Automation" is ACTIVE',
      '[14:22:00] Webhook trigger detected: Typeform submit ID: #7781',
      '[14:22:01] Routing logic check: budget="50jt"',
      '[14:22:01] Executing branch: Enterprise Template',
      '[14:22:02] PDF Generator: Merging user variables into template...',
      '[14:22:03] File created: Proposal_Enterprise_7781.pdf',
      '[14:22:04] Email: Sent PDF attachment to client...',
      'SUCCESS: Execution completed in 4.15s ✔'
    ]
  },
  'tool-integration': {
    steps: [
      { label: 'Stripe Payment', desc: 'Pembayaran sukses terdeteksi 💳', type: 'trigger' },
      { label: 'Auth verification', desc: 'Verifikasi status member 🔒', type: 'action' },
      { label: 'Discord Invite', desc: 'Kirim link komunitas otomatis 💬', type: 'action' },
    ],
    logs: [
      'STATUS: Flow "Paid Community Onboarding" is ACTIVE',
      '[08:15:30] Webhook Stripe: payment_intent.succeeded',
      '[08:15:31] Amount: Rp 250,000. Customer: Budi S.',
      '[08:15:31] Database: Upgraded membership status to Premium',
      '[08:15:32] API call: Generating Discord invite link...',
      '[08:15:33] Mailgun: Sending onboarding email with Discord invite...',
      'SUCCESS: Onboarding email sent successfully ✔'
    ]
  },
  'learning-lab': {
    steps: [
      { label: 'Git Commit Push', desc: 'Push code ke repo main 📂', type: 'trigger' },
      { label: 'Vitest Runner', desc: 'Menjalankan unit test suite 🧪', type: 'action' },
      { label: 'Deploy Webhook', desc: 'Trigger deploy ke Cloudflare 🚀', type: 'action' },
    ],
    logs: [
      'STATUS: CI/CD Pipeline "Lab Deployment" is ACTIVE',
      '[20:00:10] GitHub Event: push branch "main" by vuckuola619',
      '[20:00:12] Shell: Running "npm test"...',
      '[20:00:15] Test suite: 12 tests passed successfully.',
      '[20:00:16] Build: Compiling React static assets...',
      '[20:00:19] Wrangler: Uploading bundle to Cloudflare Workers...',
      'SUCCESS: Deployed to https://letsmakeiteasy.work/ ✔'
    ]
  }
};

const Services = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [selectedService, setSelectedService] = useState<typeof services[0] | null>(null);

  useEffect(() => {
    if (sectionRef.current) {
      const titleEl = sectionRef.current.querySelector('.section-title');
      if (titleEl) scrollReveal(titleEl as HTMLElement);
    }
    if (cardsRef.current) {
      const cards = cardsRef.current.querySelectorAll('.service-card');
      if (!prefersReducedMotion()) {
        staggerFadeIn(cards, { delay: 0.2, stagger: 0.12 });
      }
    }
  }, []);

  // Lock scroll when drawer is open
  useEffect(() => {
    if (selectedService) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedService]);

  const activeFlow = selectedService ? serviceFlows[selectedService.id] : null;

  return (
    <section id="services" ref={sectionRef} className="section-padding section-atmosphere border-t border-border bg-muted/20">
      <div className="container-site">
        <div className="section-title text-center mb-12" style={{ opacity: prefersReducedMotion() ? 1 : 0 }}>
          <span className="chip mb-4">What I Do</span>
          <h2 className="font-display uppercase font-light text-3xl sm:text-4xl tracking-[0.03em] text-foreground">
            Fokus Area
          </h2>
        </div>

        <div ref={cardsRef} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => (
            <button
              key={service.id}
              className="service-card motion-panel group min-h-full bg-card border border-border p-6 cursor-pointer text-left w-full focus:outline-none"
              style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
              onClick={() => setSelectedService(service)}
            >
              <div className="w-12 h-12 rounded-none bg-muted border border-border text-foreground flex items-center justify-center mb-4 group-hover:bg-foreground group-hover:text-background transition-colors duration-300">
                {iconMap[service.icon]}
              </div>
              <h3 className="font-sans uppercase font-bold text-sm tracking-wider mb-2 text-foreground group-hover:text-foreground">
                {service.title}
              </h3>
              <p className="text-muted-foreground text-xs leading-relaxed mb-4">
                {service.description}
              </p>
              <span className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-foreground hover:underline">
                Learn more
                <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Slide-over Drawer Overlay */}
      {selectedService && activeFlow && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 w-full h-full bg-black/60 transition-opacity duration-300 cursor-default focus:outline-none"
            onClick={() => setSelectedService(null)}
            aria-label="Close details"
          />

          {/* Slide-over Panel */}
          <div className="relative w-full max-w-lg h-full bg-background border-l border-border text-foreground p-6 sm:p-8 flex flex-col justify-between shadow-2xl z-10 overflow-y-auto animate-in slide-in-from-right duration-300 rounded-none">
            {/* Header */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <span className="chip uppercase tracking-widest text-[9px] bg-foreground text-background border border-foreground rounded-none">
                  Blueprint Flow
                </span>
                <button
                  onClick={() => setSelectedService(null)}
                  className="p-1.5 rounded-none border border-border hover:bg-muted text-foreground transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <h3 className="font-display uppercase font-light text-2xl tracking-[0.03em] mb-2 text-foreground">
                {selectedService.title}
              </h3>
              <p className="text-muted-foreground text-xs leading-relaxed mb-8">
                {selectedService.description}
              </p>

              {/* Visual Workflow Map */}
              <div className="space-y-4 mb-8">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-foreground mb-3">
                  Workflow Steps
                </h4>
                
                <div className="relative pl-6 border-l border-dashed border-border space-y-6">
                  {activeFlow.steps.map((step, idx) => (
                    <div key={idx} className="relative">
                      {/* Node Indicator */}
                      <span className="absolute -left-[32px] top-1.5 w-4 h-4 border border-border bg-foreground text-background flex items-center justify-center text-[8px] font-bold rounded-none">
                        {idx + 1}
                      </span>
                      
                      <div className="bg-muted border border-border rounded-none p-3">
                        <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground block mb-0.5">
                          {step.type}
                        </span>
                        <span className="text-xs font-bold block mb-1">
                          {step.label}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {step.desc}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Simulated Live Terminal Logs */}
            <div className="bg-card border border-border rounded-none p-4 font-mono text-[9px] text-foreground">
              <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 border border-border bg-transparent" />
                  <span className="w-2 h-2 border border-border bg-transparent" />
                  <span className="w-2 h-2 border border-border bg-transparent" />
                </div>
                <span className="text-[8px] text-muted-foreground font-bold uppercase tracking-wider">live-execution.log</span>
              </div>
              <div className="space-y-1.5 max-h-[140px] overflow-y-auto scrollbar-hide">
                {activeFlow.logs.map((log, index) => (
                  <div key={index} className={log.startsWith('SUCCESS') ? 'font-bold' : log.startsWith('STATUS') ? 'text-muted-foreground font-bold' : ''}>
                    {log}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;
